import { supabase } from '@/integrations/supabase/client';
import {
  DEFAULT_EMAIL_NOTIFICATION_PREFERENCES,
  DEFAULT_IN_APP_NOTIFICATION_PREFERENCES,
  DEFAULT_PUSH_NOTIFICATION_PREFERENCES,
  normalizeHomeRoutineReminderStrength,
  normalizeNotificationPreferenceMap,
  type ReminderStrength,
  type NotificationPreferenceMap,
} from '../../ui-foundation/features/notifications';

export type DocumentIntegrationMode = 'adulting' | 'google_drive' | 'onedrive' | 'dropbox' | 'icloud';
export type CalendarIntegrationMode = 'adulting' | 'google_calendar' | 'outlook' | 'icloud';
export type PreferredSupermarket = 'tesco' | 'sainsburys' | 'asda' | 'ocado' | 'waitrose' | 'morrisons';

export interface IntegrationSettingsRecord {
  user_id: string;
  documents_mode: DocumentIntegrationMode;
  documents_root_folder_id: string | null;
  documents_root_folder_name: string | null;
  documents_root_folder_url: string | null;
  documents_last_synced_at: string | null;
  documents_sync_status: string;
  calendar_mode: CalendarIntegrationMode;
  calendar_external_id: string | null;
  calendar_external_name: string | null;
  calendar_external_url: string | null;
  calendar_last_synced_at: string | null;
  calendar_sync_status: string;
  google_account_email: string | null;
  push_notifications_enabled: boolean;
  push_permission_status: string;
  push_last_registered_at: string | null;
  home_routine_reminder_strength: ReminderStrength;
  notification_push_preferences: NotificationPreferenceMap;
  notification_email_preferences: NotificationPreferenceMap;
  notification_in_app_preferences: NotificationPreferenceMap;
  bank_budget_auto_refresh_enabled: boolean;
  bank_budget_auto_last_applied_sync_at: string | null;
  preferred_supermarket: PreferredSupermarket | null;
  created_at?: string;
  updated_at?: string;
}

export const DEFAULT_INTEGRATION_SETTINGS: Omit<IntegrationSettingsRecord, 'user_id'> = {
  documents_mode: 'adulting',
  documents_root_folder_id: null,
  documents_root_folder_name: null,
  documents_root_folder_url: null,
  documents_last_synced_at: null,
  documents_sync_status: 'idle',
  calendar_mode: 'adulting',
  calendar_external_id: null,
  calendar_external_name: null,
  calendar_external_url: null,
  calendar_last_synced_at: null,
  calendar_sync_status: 'idle',
  google_account_email: null,
  push_notifications_enabled: false,
  push_permission_status: 'unknown',
  push_last_registered_at: null,
  home_routine_reminder_strength: 'standard',
  notification_push_preferences: DEFAULT_PUSH_NOTIFICATION_PREFERENCES,
  notification_email_preferences: DEFAULT_EMAIL_NOTIFICATION_PREFERENCES,
  notification_in_app_preferences: DEFAULT_IN_APP_NOTIFICATION_PREFERENCES,
  bank_budget_auto_refresh_enabled: false,
  bank_budget_auto_last_applied_sync_at: null,
  preferred_supermarket: null,
};

function mergeSettingsRow(userId: string, row?: Partial<IntegrationSettingsRecord> | null): IntegrationSettingsRecord {
  const nextRow = row || {};

  return {
    user_id: userId,
    ...DEFAULT_INTEGRATION_SETTINGS,
    ...nextRow,
    home_routine_reminder_strength: normalizeHomeRoutineReminderStrength(nextRow.home_routine_reminder_strength),
    notification_push_preferences: normalizeNotificationPreferenceMap(
      nextRow.notification_push_preferences,
      DEFAULT_PUSH_NOTIFICATION_PREFERENCES,
    ),
    notification_email_preferences: normalizeNotificationPreferenceMap(
      nextRow.notification_email_preferences,
      DEFAULT_EMAIL_NOTIFICATION_PREFERENCES,
    ),
    notification_in_app_preferences: normalizeNotificationPreferenceMap(
      nextRow.notification_in_app_preferences,
      DEFAULT_IN_APP_NOTIFICATION_PREFERENCES,
    ),
  };
}

export async function loadIntegrationSettings(userId: string): Promise<IntegrationSettingsRecord> {
  const { data, error } = await supabase
    .from('user_integration_settings' as any)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return mergeSettingsRow(userId, data as Partial<IntegrationSettingsRecord> | null);
}

export async function saveIntegrationSettings(
  userId: string,
  patch: Partial<IntegrationSettingsRecord>,
): Promise<IntegrationSettingsRecord> {
  const payload = {
    user_id: userId,
    ...patch,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('user_integration_settings' as any)
    .upsert(payload, { onConflict: 'user_id' })
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  notifyIntegrationSettingsChanged();
  return mergeSettingsRow(userId, data as Partial<IntegrationSettingsRecord>);
}

export function notifyIntegrationSettingsChanged() {
  window.dispatchEvent(new CustomEvent('adulting:integration-settings-changed'));
}
